import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cross, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChristianAddChild = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Basic Info, 2: Picture Login
  const [loading, setLoading] = useState(false);
  const [pictureOptions, setPictureOptions] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    birthDate: '',
    selectedPictures: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchPictureOptions();
  }, []);

  const fetchPictureOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/christian/picture-login-options`);
      setPictureOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch picture options:', error);
      // Fallback options
      setPictureOptions([
        { id: 'lion', name: 'Lion', emoji: '🦁', color: '#F59E0B' },
        { id: 'elephant', name: 'Elephant', emoji: '🐘', color: '#6B7280' },
        { id: 'rainbow', name: 'Rainbow', emoji: '🌈', color: '#EC4899' },
        { id: 'star', name: 'Star', emoji: '⭐', color: '#EAB308' },
        { id: 'heart', name: 'Heart', emoji: '❤️', color: '#EF4444' },
        { id: 'sun', name: 'Sun', emoji: '☀️', color: '#F97316' },
        { id: 'moon', name: 'Moon', emoji: '🌙', color: '#6366F1' },
        { id: 'flower', name: 'Flower', emoji: '🌸', color: '#EC4899' },
        { id: 'tree', name: 'Tree', emoji: '🌳', color: '#22C55E' },
        { id: 'fish', name: 'Fish', emoji: '🐟', color: '#3B82F6' },
        { id: 'bird', name: 'Bird', emoji: '🐦', color: '#14B8A6' },
        { id: 'butterfly', name: 'Butterfly', emoji: '🦋', color: '#8B5CF6' },
        { id: 'crown', name: 'Crown', emoji: '👑', color: '#F59E0B' },
        { id: 'cross', name: 'Cross', emoji: '✝️', color: '#7C3AED' },
        { id: 'dove', name: 'Dove', emoji: '🕊️', color: '#E5E7EB' },
        { id: 'lamb', name: 'Lamb', emoji: '🐑', color: '#F3F4F6' }
      ]);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.birthDate) newErrors.birthDate = 'Birth date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePictureToggle = (pictureId) => {
    setFormData(prev => {
      const selected = prev.selectedPictures.includes(pictureId)
        ? prev.selectedPictures.filter(id => id !== pictureId)
        : prev.selectedPictures.length < 2
          ? [...prev.selectedPictures, pictureId]
          : prev.selectedPictures;
      return { ...prev, selectedPictures: selected };
    });
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    if (formData.selectedPictures.length < 2) {
      toast.error('Please select exactly 2 pictures for login');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/christian/children`,
        {
          name: formData.name,
          birth_date: formData.birthDate,
          picture_login_images: formData.selectedPictures
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`${formData.name} has been added!`);
        navigate('/christian/parent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add child');
    } finally {
      setLoading(false);
    }
  };

  // Calculate age and level preview
  const getPreviewLevel = () => {
    if (!formData.birthDate) return null;
    const birth = new Date(formData.birthDate);
    const today = new Date();
    const age = Math.floor((today - birth) / (365.25 * 24 * 60 * 60 * 1000));
    
    if (age <= 5) return { level: 'Seeds', emoji: '🌱', ages: '3-5' };
    if (age <= 9) return { level: 'Roots', emoji: '🌿', ages: '6-9' };
    return { level: 'Warriors', emoji: '⚔️', ages: '10-13' };
  };

  const levelPreview = getPreviewLevel();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-white/10 p-4">
        <div className="flex items-center gap-4 max-w-md mx-auto">
          <button onClick={() => navigate('/christian/parent')} className="text-purple-400">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white">Add Child</h1>
            <p className="text-xs text-purple-400">Step {step} of 2</p>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto p-4">
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label htmlFor="name" className="text-white">Child's Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter name"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 mt-1"
                  />
                  {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="birthDate" className="text-white">Birth Date</Label>
                  <Input
                    id="birthDate"
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                  {errors.birthDate && <p className="text-red-400 text-sm mt-1">{errors.birthDate}</p>}
                </div>

                {/* Level Preview */}
                {levelPreview && (
                  <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-xl p-4 border border-purple-500/30">
                    <p className="text-xs text-slate-400 mb-2">Learning Level</p>
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{levelPreview.emoji}</span>
                      <div>
                        <p className="font-bold text-white">{levelPreview.level}</p>
                        <p className="text-xs text-purple-400">Ages {levelPreview.ages}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button
              onClick={handleNextStep}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
            >
              Next: Set Up Picture Login
            </Button>
          </div>
        )}

        {/* Step 2: Picture Login */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-white mb-2">Picture Password Setup</h2>
              <p className="text-slate-400 text-sm">
                Select 2 pictures that {formData.name} will use to log in. 
                This makes it easy for children to access their account!
              </p>
            </div>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-slate-400">Selected</span>
                  <span className="text-sm font-bold text-purple-400">
                    {formData.selectedPictures.length}/2
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  {pictureOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handlePictureToggle(option.id)}
                      className={`relative aspect-square rounded-2xl flex flex-col items-center justify-center transition-all ${
                        formData.selectedPictures.includes(option.id)
                          ? 'bg-purple-500/30 ring-2 ring-purple-500 scale-95'
                          : 'bg-slate-700/50 hover:bg-slate-700'
                      }`}
                    >
                      <span className="text-3xl">{option.emoji}</span>
                      <span className="text-[10px] text-slate-400 mt-1">{option.name}</span>
                      {formData.selectedPictures.includes(option.id) && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Selected Pictures Preview */}
            {formData.selectedPictures.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-400 mb-3">Picture password (order doesn't matter)</p>
                  <div className="flex items-center justify-center gap-3">
                    {formData.selectedPictures.map((id) => {
                      const option = pictureOptions.find(p => p.id === id);
                      return (
                        <div 
                          key={id} 
                          className="w-14 h-14 rounded-xl bg-slate-700 flex items-center justify-center text-3xl"
                        >
                          {option?.emoji}
                        </div>
                      );
                    })}
                    {[...Array(2 - formData.selectedPictures.length)].map((_, i) => (
                      <div 
                        key={i} 
                        className="w-14 h-14 rounded-xl bg-slate-700/50 border-2 border-dashed border-slate-600 flex items-center justify-center"
                      >
                        <span className="text-slate-500">?</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 border-slate-700 text-white hover:bg-slate-800"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || formData.selectedPictures.length !== 2}
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Child Profile'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChristianAddChild;
